/**
 * Une conversation de checkpoint avec Sam, en machine d'états pure : le starting block avant le
 * live, et la main levée pendant (le lot du live l'ouvrira sur la même machine).
 *
 * Elle remplace, pour l'espace élève, l'état de `SamConversationBase` (`useConversationState`, qui
 * ne gardait que la dernière bulle) : le fil garde chaque tour, Sam comme l'élève — l'ancien écran
 * remplaçait sa bulle et la question d'avant disparaissait.
 *
 * Ce qui se décide ici :
 *  - les réponses de Sam (`proposed_responses`) et ce que chacune ouvre (`planReply`) : partir telle
 *    quelle, écrire, choisir une date, prendre une photo, revenir à l'activité ;
 *  - la classe d'abord : c'est le serveur et Sam qui la posent (le sous-scénario « affirmer sa
 *    classe »). La machine n'invente rien — elle n'ouvre jamais de saisie libre sur un tour qui n'en
 *    propose pas (le tour de la classe n'en propose pas : l'étape ne se saute pas), ne propose pas la
 *    sortie tant que la conversation est en cours, et retient la classe que le serveur dit
 *    enregistrée (`class_affirmed`), « Autre » comprise ;
 *  - la fin : un statut qui n'est plus « en cours » et plus aucune réponse proposée ; le pétale
 *    gagné, sauf quand c'est le serveur qui a clos à la place de Sam en panne (`isAgentOutage`) ;
 *    l'avis ;
 *  - les erreurs, VISIBLES : une conversation sans rien à dire, une réponse que le serveur n'a pas
 *    rendue, une photo qui n'est pas partie. L'ancien écran fermait la conversation sur une erreur,
 *    et l'élève ne savait pas pourquoi Sam était parti.
 */
import { hasValidMessageBody, isEndedStatus, isStartingBlockContext as startingBlockContext } from "./constants"

export type CheckpointReplyAction = "photo" | "activity" | "text" | "text_homework" | "date"

/** Une réponse proposée par Sam, telle que le serveur la sert (`agent_message.proposed_responses[]`). */
export type ApiProposedResponse = { id?: number | string; body?: string; action?: string | null }

export type ApiAgentMessage = {
    id?: number | string
    conversation_id?: number | string
    body?: string | null
    proposed_responses?: ApiProposedResponse[] | null
}

/** Ce que rendent `POST conversations` et `POST conversations/{id}/response`. */
export type ApiCheckpointConversation = {
    conversation?: {
        id?: number | string
        status?: string
        trigger_context?: string
        /** Le résumé que le serveur pose en clôturant ; le seul endroit où il dit que Sam était en panne. */
        summary?: string | null
        /** Ce que Sam a noté en clôturant : l'activité qu'il lance (`next_todo_activity_id`), le contrôle déclaré… */
        key_info?: Record<string, unknown> | null
    } | null
    agent_message?: ApiAgentMessage | null
    student_responses_count?: number
    /** La classe que ce tour vient d'enregistrer (`StudentProfile::affirmClass`), sinon `null`. */
    class_affirmed?: string | null
}

export type CheckpointReply = { key: string; label: string; action: CheckpointReplyAction | null }

export type CheckpointMessage = {
    id: string
    author: "sam" | "student"
    text?: string
    /** La photo de l'élève, quand sa réponse en est une. */
    imageUri?: string
}

export type CheckpointErrorKind = "empty" | "response" | "photo"

export type CheckpointPhase =
    /** Sam prépare sa première bulle. */
    | "opening"
    /** Sam a parlé, l'élève répond. */
    | "answering"
    /** La réponse est partie, Sam réfléchit. */
    | "sending"
    /** La conversation est allée au bout : l'avis, et la sortie. */
    | "ended"
    | "error"
    | "closed"

export type CheckpointConversationState = {
    context: string
    conversationId: number | string | null
    status: string | null
    messages: CheckpointMessage[]
    replies: CheckpointReply[]
    phase: CheckpointPhase
    /** La réponse touchée : elle part, ou son geste est ouvert. */
    chosenKey: string | null
    /** Le geste ouvert par la réponse touchée. */
    gesture: "text" | "date" | "photo" | null
    textValue: string
    /** Les réponses déjà données : la barre du pétale avance. */
    answered: number
    earned: boolean
    feedback: 1 | -1 | null
    feedbackSending: boolean
    /** La classe que Sam a fait affirmer pendant cette conversation (« Autre » comprise). */
    classAffirmed: string | null
    error: CheckpointErrorKind | null
    /** La croix tout de suite : au bout de la conversation, ou sur une erreur. Le délai de 10 s est au crochet. */
    canClose: boolean
    /** Le tour en cours (un compteur) : une réponse d'un tour d'avant n'écrase pas le suivant. */
    turn: number
}

export type CheckpointEvent =
    | { type: "opened"; data: ApiCheckpointConversation | null }
    | { type: "replyChosen"; key: string; plan: ReplyPlan }
    | { type: "textChanged"; value: string }
    | { type: "gestureCancelled" }
    | { type: "sent"; turn: number; message: CheckpointMessage }
    | { type: "responseReceived"; turn: number; data: ApiCheckpointConversation | null }
    | { type: "responseFailed"; turn: number; kind: "response" | "photo" }
    | { type: "feedbackGiven"; value: 1 | -1 }
    | { type: "feedbackSettled" }
    | { type: "closed" }

/** Ce qu'ouvre une réponse touchée. */
export type ReplyPlan =
    | { kind: "send"; body: string; display: string }
    | { kind: "text" }
    | { kind: "date" }
    | { kind: "photo" }
    | { kind: "activity" }

const ACTIONS: CheckpointReplyAction[] = ["photo", "activity", "text", "text_homework", "date"]

export function initialCheckpointState(context: string): CheckpointConversationState {
    return {
        context,
        conversationId: null,
        status: null,
        messages: [],
        replies: [],
        phase: "opening",
        chosenKey: null,
        gesture: null,
        textValue: "",
        answered: 0,
        earned: false,
        feedback: null,
        feedbackSending: false,
        classAffirmed: null,
        error: null,
        canClose: false,
        turn: 0,
    }
}

const hasBody = (message: ApiAgentMessage | null | undefined): message is ApiAgentMessage & { body: string } =>
    hasValidMessageBody(message)

function repliesOf(message: ApiAgentMessage | null | undefined): CheckpointReply[] {
    return (message?.proposed_responses ?? [])
        .filter((response) => typeof response?.body === "string" && response.body !== "")
        .map((response, index) => ({
            // La clé tient au rang : deux réponses peuvent porter le même id côté serveur.
            key: `${index}`,
            label: response.body as string,
            action: ACTIONS.includes(response.action as CheckpointReplyAction)
                ? (response.action as CheckpointReplyAction)
                : null,
        }))
}

/** Un checkpoint de démarrage (`starting_block`, `starting_block_study_program`) : la règle de `constants`. */
export const isStartingBlockContext: (context: string) => boolean = startingBlockContext

/**
 * Ce que la réponse touchée ouvre. Le texte envoyé est celui de l'ancien écran, mot pour mot : le
 * service IA le lit.
 */
export function planReply(reply: CheckpointReply): ReplyPlan {
    if (reply.action === "text" || reply.action === "text_homework") return { kind: "text" }
    if (reply.action === "date") return { kind: "date" }
    if (reply.action === "photo") return { kind: "photo" }
    if (reply.action === "activity") return { kind: "activity" }
    return { kind: "send", body: reply.label, display: reply.label }
}

/** La réponse écrite, telle que le service IA l'attend. */
export function writtenResponse(reply: CheckpointReply, text: string): { body: string; display: string } {
    const body = reply.action === "text" ? `Écrire le nom du chapitre: ${text}` : `décrire le devoir : ${text}`
    return { body, display: text }
}

/** La date choisie, telle que le service IA l'attend. */
export function datedResponse(reply: CheckpointReply, date: string): { body: string; display: string } {
    return { body: `${reply.label} - ${date}`, display: `${reply.label} - ${date}` }
}

/**
 * Le résumé que le serveur pose quand le service IA n'a pas répondu (`Conversation::createWithAgent`,
 * `ConversationController::conversationResponse`) : il écrit alors à la place de Sam (« Je suis
 * désolé, une erreur s'est produite… ») et clôt la conversation en `tutor_needed`. Le statut est
 * final, mais la conversation n'est pas allée au bout : elle ne fait rien gagner. La phrase est la
 * constante `Conversation::AGENT_OUTAGE_SUMMARY` du backend, que `ConversationAgentOutageSummaryTest`
 * fige : les deux changent ensemble.
 */
export const AGENT_OUTAGE_SUMMARY = "Service d'IA indisponible durant la conversation"

/** Ce tour est celui que le serveur a écrit à la place de Sam, en panne. */
export function isAgentOutage(data: ApiCheckpointConversation | null | undefined): boolean {
    return data?.conversation?.summary === AGENT_OUTAGE_SUMMARY
}

/** La photo, telle que le service IA l'attend (le texte est fixe, la photo part en pièce jointe). */
export const PHOTO_RESPONSE = "photo prise par l'élève"

function received(
    state: CheckpointConversationState,
    data: ApiCheckpointConversation | null,
    first: boolean
): CheckpointConversationState {
    const message = data?.agent_message
    if (!hasBody(message)) {
        // Rien à montrer : jamais un écran vide sans sortie.
        return { ...state, phase: "error", error: "empty", canClose: true, chosenKey: null, gesture: null }
    }
    const status = data?.conversation?.status ?? state.status
    const replies = repliesOf(message)
    const ended = status != null && status !== "ongoing" && replies.length === 0
    const id = data?.conversation?.id ?? message.conversation_id ?? state.conversationId
    return {
        ...state,
        conversationId: id ?? null,
        status: status ?? null,
        messages: [
            ...state.messages,
            { id: `sam-${message.id ?? state.messages.length}`, author: "sam", text: message.body },
        ],
        replies,
        phase: ended ? "ended" : "answering",
        chosenKey: null,
        gesture: null,
        textValue: "",
        answered: data?.student_responses_count ?? (first ? state.answered : state.answered + 1),
        // Le pétale se gagne au bout (un statut final), une seule fois — jamais sur une panne de Sam.
        earned: state.earned || (isEndedStatus(status) && !isAgentOutage(data)),
        classAffirmed: data?.class_affirmed ?? state.classAffirmed,
        error: null,
        canClose: state.canClose || ended,
    }
}

export function checkpointReducer(
    state: CheckpointConversationState,
    event: CheckpointEvent
): CheckpointConversationState {
    switch (event.type) {
        case "opened":
            return received({ ...state, messages: [] }, event.data, true)
        case "replyChosen": {
            if (state.phase !== "answering") return state
            if (event.plan.kind === "send" || event.plan.kind === "activity") return { ...state, chosenKey: event.key }
            return { ...state, chosenKey: event.key, gesture: event.plan.kind, textValue: "" }
        }
        case "textChanged":
            return { ...state, textValue: event.value }
        case "gestureCancelled":
            return { ...state, chosenKey: null, gesture: null, textValue: "" }
        case "sent":
            return {
                ...state,
                turn: event.turn,
                phase: "sending",
                gesture: null,
                messages: [...state.messages, event.message],
            }
        case "responseReceived":
            if (event.turn !== state.turn || state.phase !== "sending") return state
            return received(state, event.data, false)
        case "responseFailed":
            if (event.turn !== state.turn) return state
            return { ...state, phase: "error", error: event.kind, canClose: true, chosenKey: null, gesture: null }
        case "feedbackGiven":
            if (state.feedback != null || state.phase !== "ended") return state
            return { ...state, feedback: event.value, feedbackSending: true }
        case "feedbackSettled":
            return { ...state, feedbackSending: false }
        case "closed":
            return { ...state, phase: "closed" }
        default:
            return state
    }
}

/** La conversation est encore en cours côté serveur : la fermer la dit interrompue. */
export function isInterruptedOnClose(state: CheckpointConversationState): boolean {
    return state.conversationId != null && state.status === "ongoing"
}
